import { Button, Modal } from '@social-solutions/component-library';
import LoadingIndicator from 'components/Generics/Loader/LoadingIndicator';
import { LoadingOverlay } from 'components/Generics/Loader/LoadingOverlay';
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import ModalHeader from './PoolModalHeader';
import * as S from './PoolParametersModal.styles';
import { ModalUserPools } from './PoolParametersTabContent';
import UserPoolRow from './UserPoolRow';

export interface IPoolParametersModalProps {
    closeModal: () => void;
    saveModal: (userPoolId: string) => Promise<void>;
    modalUserPools: ModalUserPools;
    isOpen: boolean;
    userPoolsLoaded: boolean;
    newSelection: string;
    setNewSelection: (newSelection: string) => void;
}

const PoolParametersModal = ({ closeModal, saveModal, modalUserPools, isOpen, userPoolsLoaded, newSelection, setNewSelection }: IPoolParametersModalProps ) => {
    const [inputConfirmValue, setInputConfirmValue] = useState('');
    const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);

    const updateSaveInputField = React.useCallback((input: string) => {
        setInputConfirmValue(input);
        if (newSelection === modalUserPools.currentUserPoolId) {
            setIsSaveButtonDisabled(true);
        } else {
            setIsSaveButtonDisabled(input !== 'Confirm');
        }
    }, [modalUserPools.currentUserPoolId, newSelection]);

    const handleRadioButtonChange = React.useCallback((userPoolId: string) => {
        setNewSelection(userPoolId);
        if (inputConfirmValue.length > 0) {
            updateSaveInputField('');
        }
    }, [inputConfirmValue.length, setNewSelection, updateSaveInputField]);

    const handleInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        updateSaveInputField(event.target.value);
    }, [updateSaveInputField]);

    const handleSave = async () => {
        await saveModal(newSelection);
        updateSaveInputField('');
    };

    useEffect(() => {
        const selectedPool = modalUserPools.userPools.find(pool => pool.userPoolId === newSelection);
        if (selectedPool && !selectedPool?.available) {
            setNewSelection(modalUserPools.currentUserPoolId);
        }
    }, [modalUserPools, newSelection, setNewSelection]);

    return (
        <Modal
            ariaHideApp={false}
            blackout={false}
            isOpen={isOpen}
            onRequestClose={closeModal}
            withClose={false}
            title={<ModalHeader closeModal={closeModal} />}
            style={S.ModalCustomStyle}
        >
            <S.ModalBodyWrapper>
                <S.WarningContainer>
                    <S.WarningText>
                        <S.WarningIcon />
                        <S.BoldWarning>Warning:</S.BoldWarning> Changing the SSO Pool that is currently selected will automatically <S.BoldText>delete all</S.BoldText> associated configurations.
                    </S.WarningText>
                </S.WarningContainer>
                <S.RowScroller>
                    <S.ContentWithOverlay>
                        {!userPoolsLoaded && (<LoadingOverlay>
                            <S.FixedContainer>
                                <LoadingIndicator />
                            </S.FixedContainer>
                        </LoadingOverlay>)}
                        {modalUserPools.userPools.map((userPool, index) => (
                            <UserPoolRow
                                key={index}
                                userPool={userPool}
                                isSelected={newSelection === userPool.userPoolId}
                                onRadioChange={handleRadioButtonChange}
                                isCurrentSelection={modalUserPools.currentUserPoolId === userPool.userPoolId}
                            />
                        ))}
                    </S.ContentWithOverlay>
                </S.RowScroller>
                <S.ButtonWrapper>
                    <S.ConfirmText>Type the word <S.BoldText>"Confirm"</S.BoldText> into this field to enable save:</S.ConfirmText>
                    <S.ConfirmInputWrapper>
                        <input
                            type="text"
                            value={inputConfirmValue}
                            onChange={handleInputChange}
                        />
                    </S.ConfirmInputWrapper>
                    <Button disabled={isSaveButtonDisabled} onClick={handleSave}><Trans>BUTTON.LABEL_SAVE</Trans></Button>
                </S.ButtonWrapper>
            </S.ModalBodyWrapper>
        </Modal>
    );
};

export default PoolParametersModal;
